import { apiSlice } from "../services/apiSlice";


interface GetPagesQuery {
    tags?: string;       // optional tag filter
    query?: string; // optional status filter
    filter?: string; // optional status filter
    statusId?: number;
    visibility?: 'public' | 'private' | 'members-only';
    isStandard?: boolean;
    pageType?: 'internal' | 'external';
    page?: number;
    perPage?: number;
}
export const pageBuilderApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({


        logoPreSignedUrl: builder.mutation({
            query: (data) => ({
                url: `/builder/websites/presigned-url`,
                method: "POST",
                body: {
                    ...data,
                },
            }),
        }),

        createWebsite: builder.mutation({
            query: (data) => ({
                url: `/builder/websites`,
                method: "POST",
                body: {
                    ...data,
                },
            }),
            invalidatesTags: ["builder"],
        }),

        getSettingDetails: builder.query<any, void>({
            query: () => ({
                url: `/builder/websites`,
            }),
            providesTags: ["builder"],
        }),

        updateSettingDetails: builder.mutation({
            query: (data) => ({
                url: `/builder/websites`,
                method: "PUT",
                body: {
                    ...data,
                },
            }),
            invalidatesTags: ["builder"],
        }),

        getPages: builder.query<any, GetPagesQuery | void>({
            query: (arg) => {
                const params = new URLSearchParams();

                if (arg?.tags) params.append('tags', arg.tags);
                if (arg?.query) params.append('query', arg.query);
                if (arg?.filter) params.append('filter', arg.filter);
                if (typeof arg?.statusId === 'number') params.append('statusId', String(arg.statusId));
                if (arg?.visibility) params.append('visibility', arg.visibility);
                if (typeof arg?.isStandard === 'boolean') params.append('isStandard', String(arg.isStandard));
                if (arg?.pageType) params.append('pageType', arg.pageType);
                if (typeof arg?.page === 'number') params.append('page', String(arg.page));
                if (typeof arg?.perPage === 'number') params.append('perPage', String(arg.perPage));

                const queryString = params.toString();
                return {
                    url: queryString
                        ? `/builder/websites/page-listing?${queryString}`
                        : '/builder/websites/page-listing',
                };
            },
            providesTags: ['Pages'],
        }),
        createPage: builder.mutation({
            query: (data) => ({
                url: `/builder/websites/page`,
                method: "POST",
                body: {
                    ...data,
                },
            }),
        }),

        getPageTags: builder.query<any, void>({
            query: () => ({
                url: `/builder/websites/page-tags`,
            }),
            providesTags: ['PageTags'],
        }),
        getSinglePage: builder.query<any, number>({
            query: (id) => ({
                url: `/builder/websites/page/${id}`,
            }),
        }),

        updatePageDetails: builder.mutation({
            query: ({ id, data }) => ({
                url: `/builder/websites/page/${id}`,
                method: "PUT",
                body: {
                    ...data,
                },
            }),
            invalidatesTags: ["builder"],
        }),

        deleteWebistePage: builder.mutation({
            query: (id: number) => ({
                url: `/builder/websites/page/${id}`,
                method: 'DELETE',
            }),
        }),

        duplicateWebistePage: builder.mutation({
            query: (id: number) => ({
                url: `/builder/websites/page/${id}/duplicate`,
                method: 'POST',
            }),
        }),

        // getMenus: builder.query<any, string>({
        //     query: (accessKey) => ({
        //         url: `/builder/websites/menus?accessKey=${accessKey}`,
        //     }),
        // }),

        getMenus: builder.query<any, string | void>({
            query: (accessKey) => ({
                url: accessKey
                    ? `/builder/websites/menus?accessKey=${accessKey}`
                    : `/builder/websites/menus`,
            }),
            providesTags: ['Menus'],
        }),


        createMenu: builder.mutation({
            query: (data) => ({
                url: `/builder/websites/menus`,
                method: "POST",
                body: {
                    ...data,
                },
            }),
        }),

        getPageElements: builder.query<any, string | void>({
            query: (searchQuery) => ({
                url: searchQuery
                    ? `/builder/websites/element-templates?query=${encodeURIComponent(searchQuery)}`
                    : `/builder/websites/element-templates`,
            }),
        }),

        publishPageLayout: builder.mutation({
            query: ({ id, data, statusId }) => ({
                url: `/builder/websites/page/${id}/build?statusId=${statusId}`,
                method: "PUT",
                body: {
                    ...data,
                },
            }),
        }),

        publishHeaderFooter: builder.mutation({
            query: (data) => ({
                url: `/builder/websites/menus`,
                method: "PUT",
                body: {
                    ...data,
                },
            }),
        }),

        getPageLayout: builder.query<any, number>({
            query: (id) => ({
                url: `/builder/websites/page/${id}`,
            }),
        }),

        getPageDirectory: builder.query<any, { 
            scope: string; 
            search?: string; 
            page?: number; 
            perPage?: number;
            queryPayload?: {
                selectedColumns?: string[];
                criteria?: {
                    logic: 'AND' | 'OR';
                    conditions: Array<{
                        field: string;
                        operator: string;
                        value: any;
                    }>;
                };
            };
        }>({
            query: ({ scope, search, page, perPage, queryPayload }) => {
                const params = new URLSearchParams({ scope });
                if (search) params.append('query', search);
                if (typeof page === 'number') params.append('page', String(page));
                if (typeof perPage === 'number') params.append('perPage', String(perPage));
                // Include queryPayload (selectedColumns and criteria) in the request
                if (queryPayload) {
                    params.append('queryPayload', JSON.stringify(queryPayload));
                }
                return {
                    url: `/builder/websites/directory-fields?${params.toString()}`,
                };
            },
        }),

        getDnsSetup: builder.query<any, void>({
            query: () => ({
                url: `/builder/websites/dns-setup`,
            }),
        }),

        getDnsVerification: builder.query<any, void>({
            query: () => ({
                url: `/builder/websites/dns-verify`,
            }),
        }),

        dnsRetry: builder.mutation({
            query: () => ({
                url: `/builder/websites/dns-retry`,
                method: "POST",

            }),
        }),

        customDomain: builder.mutation({
            query: ({ data }) => ({
                url: `/builder/websites`,
                method: "PUT",
                body: {
                    ...data,
                },
            }),
            invalidatesTags: ["builder"],
        }),

        getPublicDirectory: builder.query({
            query: (data) => ({
                url: `/builder/websites/directory-search`,
                method: "POST",
                body: {
                    ...data,
                },
            }),
        }),

    }),
});

export const {
    useLogoPreSignedUrlMutation,
    useCreateWebsiteMutation,
    useGetSettingDetailsQuery,
    useUpdateSettingDetailsMutation,
    useGetPagesQuery,
    useCreatePageMutation,
    useGetPageTagsQuery,
    useGetSinglePageQuery,
    useUpdatePageDetailsMutation,
    useDeleteWebistePageMutation,
    useDuplicateWebistePageMutation,
    useGetMenusQuery,
    useCreateMenuMutation,
    useGetPageElementsQuery,
    usePublishPageLayoutMutation,
    useGetPageLayoutQuery,
    usePublishHeaderFooterMutation,
    useGetPageDirectoryQuery,
    useGetDnsSetupQuery,
    useGetDnsVerificationQuery,
    useDnsRetryMutation,
    useCustomDomainMutation,
    useGetPublicDirectoryQuery
} = pageBuilderApiSlice;
